import { useState, useRef, useEffect } from "react";
import { Search, ChevronDown, X } from "lucide-react";
import { useUsers } from "../../hooks/useUsers";
import { useBizUsers } from "../../hooks/useBizAdmin";

export interface SelectedUser {
  userId: string;
  name: string;
  username: string;
  position: string;
  department: string;
}

interface UserPickerProps {
  value: SelectedUser | null;
  onChange: (user: SelectedUser | null) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  businessId?: string;
  /** User IDs to hide from results (e.g. employees already in the business). */
  excludeUserIds?: string[];
}

export function UserPicker({
  value,
  onChange,
  placeholder = "Search employees…",
  disabled = false,
  error,
  businessId,
  excludeUserIds,
}: UserPickerProps) {
  // Always call both hooks; enable only the relevant one to satisfy rules of hooks
  const { data: globalData, isLoading: globalLoading } = useUsers("", !businessId);
  const { data: bizData, isLoading: bizLoading } = useBizUsers(businessId ?? "");

  const allUsers = businessId
    ? (bizData ?? [])
    : (globalData?.pages.flatMap((page) => page.items) ?? []);
  const excludeSet = new Set(excludeUserIds ?? []);
  const users = excludeSet.size ? allUsers.filter((u) => !excludeSet.has(u.userId)) : allUsers;
  const isLoading = businessId ? bizLoading : globalLoading;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = users.filter((u) => {
    const fullName = `${u.firstName} ${u.lastName}`.toLowerCase();
    const q = query.toLowerCase();
    return (
      fullName.includes(q) ||
      u.username.toLowerCase().includes(q) ||
      u.position.toLowerCase().includes(q) ||
      u.department.toLowerCase().includes(q)
    );
  });

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelect = (user: (typeof users)[0]) => {
    onChange({
      userId: user.userId,
      name: `${user.firstName} ${user.lastName}`.trim(),
      username: user.username,
      position: user.position,
      department: user.department,
    });
    setOpen(false);
    setQuery("");
  };

  const handleClear = () => {
    onChange(null);
    setQuery("");
  };

  const handleOpen = () => {
    if (disabled) return;
    setOpen(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger */}
      <button
        type="button"
        onClick={handleOpen}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[#2447d9] ${
          error
            ? "border-[#dc2626]"
            : open
            ? "border-[#2447d9]"
            : "border-[#e2e8f0]"
        } bg-white disabled:cursor-not-allowed disabled:opacity-60`}
      >
        {value ? (
          <span className="flex items-center gap-2 text-[#0f172a] min-w-0">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#eff3ff] text-[10px] font-bold text-[#2447d9] uppercase">
              {value.name.charAt(0)}
            </span>
            <span className="flex flex-col text-left min-w-0">
              <span className="truncate font-medium">{value.name}</span>
              <span className="truncate text-xs text-[#64748b]">{value.position}</span>
            </span>
          </span>
        ) : (
          <span className="text-[#94a3b8]">{placeholder}</span>
        )}
        <span className="flex shrink-0 items-center gap-1 ml-2">
          {value && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); handleClear(); }}
              onKeyDown={(e) => e.key === "Enter" && (e.stopPropagation(), handleClear())}
              className="flex h-5 w-5 items-center justify-center rounded-full text-[#94a3b8] hover:bg-[#f1f5f9] hover:text-[#64748b]"
              aria-label="Clear selection"
            >
              <X className="h-3 w-3" />
            </span>
          )}
          <ChevronDown className={`h-4 w-4 text-[#94a3b8] transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className="absolute z-50 mt-1 w-full rounded-xl border border-[#e2e8f0] bg-white shadow-[0_4px_16px_0_rgb(0_0_0/0.12)] overflow-hidden"
          role="listbox"
        >
          {/* Search input */}
          <div className="flex items-center gap-2 border-b border-[#f1f5f9] px-3 py-2">
            <Search className="h-4 w-4 shrink-0 text-[#94a3b8]" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type to search…"
              className="flex-1 bg-transparent text-sm text-[#0f172a] placeholder:text-[#94a3b8] focus:outline-none"
            />
          </div>

          {/* Options */}
          <ul className="max-h-56 overflow-y-auto py-1">
            {isLoading && (
              <li className="px-4 py-3 text-center text-xs text-[#94a3b8]">Loading employees…</li>
            )}
            {!isLoading && filtered.length === 0 && (
              <li className="px-4 py-3 text-center text-xs text-[#94a3b8]">No employees found</li>
            )}
            {!isLoading && filtered.map((u) => {
              const name = `${u.firstName} ${u.lastName}`.trim();
              const isSelected = value?.userId === u.userId;
              return (
                <li
                  key={u.userId}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => handleSelect(u)}
                  className={`flex cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-[#f8fafc] ${
                    isSelected ? "bg-[#eff3ff]" : ""
                  }`}
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#eff3ff] text-[10px] font-bold text-[#2447d9] uppercase">
                    {name.charAt(0)}
                  </span>
                  <span className="min-w-0">
                    <p className="font-medium text-[#0f172a] truncate">{name}</p>
                    <p className="text-xs text-[#64748b] truncate">{u.position} · {u.department}</p>
                  </span>
                  {isSelected && (
                    <span className="ml-auto shrink-0 text-[#2447d9]">✓</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {error && (
        <p className="mt-1 text-xs text-[#dc2626]" role="alert">{error}</p>
      )}
    </div>
  );
}
